import { Dialog } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton, {
  OutlineCustomButton,
} from "shared/components/inputButton";
import { GetContractDetailsActon } from "store/actions/ContractActions";
import { formatDateToMMDDYYYY } from "utils/common";

export default function ProducerDocument({
  contractDetails,
  sign,
  setSign,
  onSignSubmit,
  handleClear,
  currentSigner,
  setCurrentSigner,
}) {
  const [visible, setVisible] = useState(false);

  const date = formatDateToMMDDYYYY(contractDetails?.payload?.agreementDate);
  const deadline = formatDateToMMDDYYYY(
    contractDetails?.payload?.details?.deadline
  );

  const allClauses = [
    {
      name: "representation",
      title: "REPRESENTATION AND WARRANTIES",
      content: (
        <>
          <p className="mb-3">
            a. Producer hereby represents and warrants that: (a) Producer is
            free to enter into and perform this agreement, and are not and will
            not be under any disability, restriction or prohibition, contractual
            or otherwise with respect to Producer’s right to execute this
            agreement, grant all of the rights granted to Artist hereunder and
            fully perform each and every term hereof; (b) All materials
            furnished by Producer in connection with the Master and Composition,
            as applicable, shall be original and not infringe upon or violate
            the rights of any third parties; and (c) Producer is not subject to
            a recording agreement or any other restriction that would require
            Artist to obtain any third-party clearances in connection with
            Producer's services hereunder.
          </p>
          <p className="mb-3">
            b. Artist hereby represents and warrants that Artist is free to
            enter and perform this Agreement, and is not under any disability,
            restriction or prohibition, contractual or otherwise, with respect
            to Artist’s right to execute this Agreement and Artist’s right to
            fully perform each and every term and provision hereof.
          </p>
        </>
      ),
    },
    {
      name: "indemnification",
      title: "INDEMNIFICATION",
      content: (
        <>
          <p className="mb-3">
            The parties each hereby agree to mutually indemnify and hold each
            other harmless against any third-party claim, liability, documented
            cost and expense (including reasonable outside attorneys’ fees and
            reasonable, actual and documented court costs) in connection with
            any third-party claim which is inconsistent with any agreement,
            covenant, representation, or warranty made by that party hereunder,
            provided such claim is reduced to a final, adverse judgment by a
            court of competent jurisdiction or settled with the indemnifying
            party’s prior written consent.
          </p>
        </>
      ),
    },
    {
      name: "notice",
      title: "NOTICE",
      content: (
        <>
          <p className="mb-3">
            Notices hereunder shall be in writing, via email (if written
            confirmation may be obtained), facsimile (if written confirmation
            may be obtained), or certified mail. The date and time of
            confirmation (for facsimile or email) or certification shall be the
            date and time of such notice, unless such date and time do not fall
            between 9:00 a.m. and 5:00 p.m. (recipient’s time zone) on a
            business day, in which case the date and time shall be deemed to be
            9:00 a.m. on the next business day.
          </p>
        </>
      ),
    },
    {
      name: "remedies",
      title: "Remedies",
      content: (
        <>
          <p className="mb-3">
            <strong>
              {contractDetails?.payload?.details?.remedies?.procedure ||
                "__________________________"}
            </strong>
            . This Agreement shall be governed by the laws of
            <strong>
              {contractDetails?.payload?.details?.remedies?.jurisdiction ||
                "__________________________"}
            </strong>
            .
            <strong>
              {contractDetails?.payload?.details?.remedies
                ?.preagreedCompensation || "__________________________"}
            </strong>
          </p>
        </>
      ),
    },
    {
      name: "entireContract",
      title: "ENTIRE CONTRACT",
      content: (
        <>
          <p className="mb-3">
            This Agreement contains the entire understanding of the parties as
            to the subject matter hereof, and all prior Agreements as to such
            subject matter have been merged herein. Producer acknowledges that
            Producer has had the opportunity to consult with counsel regarding
            this Agreement. If any provision of this Agreement is invalid, void
            or unenforceable, the remainder of this Agreement shall remain in
            full force and effect. This Agreement may not be altered in any way
            except by an instrument signed by the parties. This Agreement may be
            signed in counterpart, each of which shall be deemed an original,
            but all of which together shall constitute the Agreement.
          </p>
        </>
      ),
    },
    {
      name: "independentContract",
      title: "INDEPENDENT CONTRACTOR",
      content: (
        <>
          <p className="mb-3">
            Side Artist is an independent contractor and nothing in this
            Agreement is intended or shall be construed to create an
            employer-employee, joint venture, or business relationship other
            than independent contractor as between the Company and Side Artist.
          </p>
        </>
      ),
    },
  ];

  const displayedClauses = allClauses.filter((clause) =>
    contractDetails?.payload?.clauses?.some((c) => c.name === clause.name)
  );

  return (
    <div>
      <div className="bg-white shadow-1 p-5 ">
        <h1 className="mb-5 text-center">
          <strong>
            {contractDetails?.payload?.agreementType || "____________"}
          </strong>
        </h1>
        <p className="mb-3">
          This producer agreement (the "
          <strong>{contractDetails?.payload?.agreementName}")</strong>, is
          entered into and made effective as of{" "}
          <strong>{date || "____________"}</strong>.
        </p>
        <p className="mb-3">By and Between </p>
        <p className="mb-3">
          <strong>
            {contractDetails?.payload?.primaryArtist?.fullLegalName ||
              "____________"}
          </strong>
          , p/k/a{" "}
          <strong>
            {contractDetails?.payload?.primaryArtist?.name || "____________"}
          </strong>
          (the “Artist”) with an address at{" "}
          <strong>
            {contractDetails?.payload?.primaryArtist?.address || "____________"}
          </strong>
          , And{" "}
          <strong>
            {contractDetails?.payload?.details?.producerName || "____________"}
          </strong>{" "}
          (the “Producer”) with an address at{" "}
          <strong>
            {contractDetails?.payload?.details?.producerAddress ||
              "____________"}
          </strong>
          .
        </p>
        <h3 className="mt-5 mb-3">1. SERVICES</h3>
        <p className="mb-3">
          Artist hereby engages the Producer for the services of{" "}
          <strong>
            {contractDetails?.payload?.details?.services || "___________"}
          </strong>{" "}
          of the sound recording (the “Recording”) of the musical work titled “
          <strong>
            {contractDetails?.payload?.details?.trackName || "____________"}
          </strong>
          ” (the “Song”) which is to be released by the Artist.
        </p>
        <p className="mb-3">
          <strong>
            {contractDetails?.payload?.details?.approvalProcess ||
              "____________"}
          </strong>
        </p>
        <p className="mb-3">
          <strong>
            {contractDetails?.payload?.details?.deliveryMethod ||
              "____________"}
          </strong>
          . The delivery deadline is on or before{" "}
          <strong>{deadline || "____________"}</strong>.
        </p>
        <h3 className="mt-5 mb-3">2. FLAT FEE</h3>
        <p className="mb-3">
          The Producer is entitled to{" "}
          <strong>
            {contractDetails?.payload?.details?.fee || "____________"}{" "}
          </strong>
          for the services.
        </p>
        <h3 className="mt-5 mb-3">3. ROYALTIES</h3>
        <p className="mb-3">
          a. As consideration for Producer's services hereunder, Artist shall
          pay or shall cause its distributor to pay Producer a royalty (the
          “Producer Royalty”) equal to{" "}
          <strong>
            {contractDetails?.payload?.details?.masterRoyalty || "____________"}
          </strong>
          . As used herein the “net profits” shall mean the “all-in” royalties
          and/or “net receipts” payable to Artist in connection with all
          exploitations of the Master, including the applicable third-party
          distribution agreement.
        </p>
        <p className="mb-3">
          b.{" "}
          {contractDetails?.payload?.details?.recoupabilityClause ||
            "____________"}
          .
        </p>
        <h3 className="mt-5 mb-3">4. CREDIT</h3>
        <p className="mb-3">
          Artist shall credit or cause applicable third parties to credit
          Producer on the packaging, labels, and liner notes (and wherever
          digital metadata permits) of records in all configurations derived
          from the Master, and on the back covers of singles of the Master in
          substantially the following form:{" "}
          <strong>
            {contractDetails?.payload?.details?.credit || "____________"}
          </strong>{" "}
          Such credit shall be subject to the customary exclusions of the
          distributor, if any, but all other aspects of such credit shall be
          subject to Artist’s discretion. No casual or inadvertent failure to
          comply with the credit requirements set forth herein, nor the failure
          of any third party to comply, shall be a breach of this Agreement,
          provided that upon receipt of written notice from Producer, Artist
          shall use reasonable efforts to cure or cause any third party to cure
          prospectively any such failure to comply.
        </p>
        <h3 className="mt-5 mb-3">5. ACCOUNTING</h3>
        {contractDetails?.payload?.details?.accountingType === "na" ? (
          <p className="mb-3">N/A</p>
        ) : (
          <>
            <p className="mb-3">
              a. Artist shall execute from time to time customary letters of
              direction to the applicable distributor and other third parties
              (e.g., with respect to Direct Monies), to direct and authorize
              payments to Producer of royalties due to Producer at the same time
              as sums are paid to or on behalf of Artist. In absence of direct
              payment from third parties, the Artist shall pay the Producer
              Royalty and provide Producer with accounting statements on a{" "}
              <strong>
                {contractDetails?.payload?.details?.accountingType ||
                  "____________"}
              </strong>{" "}
              and the first payment should commence within thirty (30) days of
              Artist’s receipt thereof from the applicable third-party (e.g.
              distributor or label). Notwithstanding the foregoing, if the
              Producer Royalty accrued during any such accounting period is less
              than One Hundred Dollars ($100), then the balance will be rolled
              over to the next accounting period until more than One Hundred
              Dollars ($100) is payable to Producer hereunder.
            </p>
            <p className="mb-3">
              b. Producer shall have the right to engage a certified public
              accountant or licensed attorney to audit the Artist’s books and
              records solely as they pertain to the calculation and payment of
              monies to Producer hereunder to verify the accuracy of such
              statements at Producer's sole expense, at the place where the
              Artist maintains such records. Any objection relating to any
              accounting statement, or any lawsuit arising therefrom, must be
              made (and any lawsuit commenced),{" "}
              <strong>
                {contractDetails?.payload?.details?.accountingCost ||
                  "____________"}
              </strong>
              . Subject to the foregoing, if an underpayment is found in
              relation to any given statement, Artist shall promptly pay all
              amounts shown to be due to the Producer thereunder.
            </p>
          </>
        )}
        
        {displayedClauses.map((clause, index) => (
          <div key={clause.name}>
            <h3 className="mt-5 mb-3">
              {index + 6}. {clause.title}
            </h3>
            {clause.content}
          </div>
        ))}
        <p className="mt-7 mb-3">ACCEPTED AND AGREED TO BY:</p>

        {/* Artist Section */}
        <p className="mt-6 mb-3">Artist</p>
        <h3 className="mt-6 mb-3">
          <div style={{ position: "relative" }}>
            {contractDetails?.payload?.signatures?.some(
              (sig) => sig.role === "artist"
            ) ? (
              contractDetails.payload.signatures.find(
                (sig) => sig.role === "artist"
              ).signature ? (
                <img
                  src={
                    contractDetails.payload.signatures.find(
                      (sig) => sig.role === "artist"
                    ).signature
                  }
                  style={{ width: "150px", height: "auto" }}
                  alt="Artist Signature"
                />
              ) : (
                <div
                  style={{
                    minWidth: "150px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "4px",
                    fontFamily: "cursive",
                  }}
                >
                  {contractDetails.payload.signatures.find(
                    (sig) => sig.role === "artist"
                  ).typedSignature || " "}
                </div>
              )
            ) : sign.artist?.signature ? (
              <img
                src={sign.artist.signature}
                style={{ width: "150px", height: "auto" }}
                alt="Artist Signature"
              />
            ) : (
              <div
                style={{
                  minWidth: "150px",
                  borderBottom: "1px solid #000",
                  paddingBottom: "4px",
                  fontFamily: "cursive",
                }}
              >
                {sign.artist?.typedSignature || " "}
              </div>
            )}

            {!contractDetails?.payload?.signatures?.some(
              (sig) => sig.role === "artist"
            ) &&
              !sign.artist?.signature &&
              !sign.artist?.typedSignature && (
                <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                  <CustomButton
                    label="Sign"
                    onClick={() => onSignSubmit("artist")}
                    disabled={
                      !!sign.producer?.signature ||
                      !!sign.producer?.typedSignature
                    }
                  />
                </div>
              )}

            {!contractDetails?.payload?.signatures?.some(
              (sig) => sig.role === "artist"
            ) &&
              (sign.artist?.signature || sign.artist?.typedSignature) && (
                <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                  <CustomButton
                    label="Resign"
                    onClick={() => onSignSubmit("artist")}
                    disabled={
                      !!sign.producer?.signature ||
                      !!sign.producer?.typedSignature
                    }
                  />
                  <OutlineCustomButton
                    label="Clear"
                    onClick={() => {
                      if (typeof handleClear === "function") {
                        handleClear("artist");
                      } else {
                        setSign((prev) => ({
                          ...prev,
                          artist: {
                            signature: "",
                            typedSignature: "",
                          },
                        }));
                      }
                    }}
                  />
                </div>
              )}
          </div>
        </h3>

        {/* Producer Section */}
        <p className="mt-6 mb-3">Producer</p>
        <h3 className="mt-6 mb-3">
          <div style={{ position: "relative" }}>
            {contractDetails?.payload?.signatures?.some(
              (sig) => sig.role === "producer"
            ) ? (
              contractDetails.payload.signatures.find(
                (sig) => sig.role === "producer"
              ).signature ? (
                <img
                  src={
                    contractDetails.payload.signatures.find(
                      (sig) => sig.role === "producer"
                    ).signature
                  }
                  style={{ width: "150px", height: "auto" }}
                  alt="Producer Signature"
                />
              ) : (
                <div
                  style={{
                    minWidth: "150px",
                    borderBottom: "1px solid #000",
                    paddingBottom: "4px",
                    fontFamily: "cursive",
                  }}
                >
                  {contractDetails.payload.signatures.find(
                    (sig) => sig.role === "producer"
                  ).typedSignature || " "}
                </div>
              )
            ) : sign.producer?.signature ? (
              <img
                src={sign.producer.signature}
                style={{ width: "150px", height: "auto" }}
                alt="Producer Signature"
              />
            ) : (
              <div
                style={{
                  minWidth: "150px",
                  borderBottom: "1px solid #000",
                  paddingBottom: "4px",
                  fontFamily: "cursive",
                }}
              >
                {sign.producer?.typedSignature || " "}
              </div>
            )}

            {!contractDetails?.payload?.signatures?.some(
              (sig) => sig.role === "producer"
            ) &&
              !sign.producer?.signature &&
              !sign.producer?.typedSignature && (
                <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                  <CustomButton
                    label="Sign"
                    onClick={() => onSignSubmit("producer")}
                    disabled={
                      !!sign.artist?.signature || !!sign.artist?.typedSignature
                    }
                  />
                </div>
              )}

            {!contractDetails?.payload?.signatures?.some(
              (sig) => sig.role === "producer"
            ) &&
              (sign.producer?.signature || sign.producer?.typedSignature) && (
                <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
                  <CustomButton
                    label="Resign"
                    onClick={() => onSignSubmit("producer")}
                    disabled={
                      !!sign.artist?.signature || !!sign.artist?.typedSignature
                    }
                  />
                  <OutlineCustomButton
                    label="Clear"
                    onClick={() => {
                      if (typeof handleClear === "function") {
                        handleClear("producer");
                      } else {
                        setSign((prev) => ({
                          ...prev,
                          producer: {
                            signature: "",
                            typedSignature: "",
                          },
                        }));
                      }
                    }}
                  />
                </div>
              )}
          </div>
        </h3>
      </div>
    </div>
  );
}
